import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Drawer, Empty, Input, InputNumber, Radio, Select, Space, Spin, Tag, Tooltip, Typography, message } from 'antd';
import { PlusOutlined, DeleteOutlined, SaveOutlined, UndoOutlined, LoadingOutlined } from '@ant-design/icons';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Material } from '@babylonjs/core/Materials/material';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3, Quaternion } from '@babylonjs/core/Maths/math.vector';
import { RotationGizmo } from '@babylonjs/core/Gizmos/rotationGizmo';
import { UtilityLayerRenderer } from '@babylonjs/core/Rendering/utilityLayerRenderer';
import { GLTF2Export } from '@babylonjs/serializers/glTF/2.0';
import '@babylonjs/core/Rendering/outlineRenderer';
import { ingestionApi } from '../ingestionApi';
import { geometryGroupKey, sortedVariants, variationLabel } from '../ingestionHelpers';
import { setupScene, frameCamera, frontDirectionOf, worldBounds, loadModelFromBlob } from './babylonScene';
import {
    BEDDING_EXTRAS_KEY,
    BLANKET_CONTAINER_MESH_NAME,
    BLANKET_MESH_NAME,
    BLANKET_STYLES,
    HEAD_AT_PLUS_X_TYPES,
    MATTRESS_MESH_NAME,
    defaultBlanketStyle,
    isBedPartMeshName,
    isBedType,
    isBlanketMeshName,
    isMattressMeshName,
    isSeparateBlanket,
    loadBedParts,
} from './bedParts';

const { Text } = Typography;

// The catalog container convention (mirrors client meshesSchema CONTAINER/INVISIBLE
// and the publisher's CONTAINER_MARKER — kept in sync by hand across the deploys).
const CONTAINER_PREFIX = '$$container_';
const INVISIBLE_MARKER = 'invisible';
const DEFAULT_PLANE_NAME = `${CONTAINER_PREFIX}${INVISIBLE_MARKER}`;

const isContainerInvisible = (n) => n.startsWith(CONTAINER_PREFIX) && n.includes(INVISIBLE_MARKER);

/** A mesh that follows the catalog container convention (mirrors client meshesSchema `isMeshContainer`). */
export const isContainerMeshName = (name) => typeof name === 'string' && name.startsWith(CONTAINER_PREFIX);

// The bedding is two selectable, movable things sharing the plane selection /
// arrow-key machinery under these ids: the mattress (which carries the whole
// group) and the blanket (which moves relative to the mattress).
const BED_ID = 'bed';
const BLANKET_ID = 'blanket';
const BED_OUTLINE_COLOR = new Color3(1, 0.2, 0.2);

// Where a fresh mattress lands on a bare frame, in the catalog orientation (beds
// lie along X, head end at +X, long side facing −Z): almost the frame's footprint,
// beds pulled toward the foot (−X) end, resting a third of the way up — the admin
// nudges it from there. Thickness keeps the source bedding's proportion.
const defaultBedSpec = (bb, itemType, { mattressSize }) => {
    const width = round(bb.width * 0.9);
    return {
        width,
        depth: round(bb.depth * 0.85),
        height: round((width * mattressSize.y) / mattressSize.x),
        blanket: defaultBlanketStyle(itemType),
        mode: 'xz',
        blanketMode: 'xz',
    };
};

const beddingExtras = ({ spec, group, blanketOffset }) => ({
    width: spec.width,
    depth: spec.depth,
    height: spec.height,
    blanket: spec.blanket,
    position: group.position.asArray(),
    rotation: group.rotationQuaternion.asArray(),
    blanketOffset: blanketOffset.asArray(),
});

/** The bedding settings a model saved by this editor carries, or null (also when its blanket style is gone). */
const savedBeddingOf = (meshes) => {
    const mesh = meshes.find((m) => m.metadata?.gltf?.extras?.[BEDDING_EXTRAS_KEY]);
    const saved = mesh?.metadata.gltf.extras[BEDDING_EXTRAS_KEY];
    const finite = (list, length) => Array.isArray(list) && list.length === length && list.every(Number.isFinite);
    if (
        !saved ||
        ![saved.width, saved.depth, saved.height].every(Number.isFinite) ||
        !finite(saved.position, 3) ||
        !finite(saved.rotation, 4) ||
        !finite(saved.blanketOffset, 3) ||
        (saved.blanket != null && !BLANKET_STYLES.some((s) => s.id === saved.blanket))
    ) {
        return null;
    }
    return {
        mesh,
        spec: {
            width: saved.width,
            depth: saved.depth,
            height: saved.height,
            blanket: saved.blanket ?? null,
            mode: 'xz',
            blanketMode: 'xz',
        },
        position: Vector3.FromArray(saved.position),
        rotation: Quaternion.FromArray(saved.rotation),
        blanketOffset: Vector3.FromArray(saved.blanketOffset),
    };
};
const defaultBedPosition = (bb, itemType, spec) =>
    new Vector3(
        HEAD_AT_PLUS_X_TYPES.includes(itemType) ? bb.min.x + bb.width * 0.05 + spec.width / 2 : bb.center.x,
        bb.min.y + bb.height * 0.3,
        bb.center.z,
    );

const round = (n) => Math.round(n * 1000) / 1000;
const fmt = (n) => (n != null ? n.toFixed(2) : '—');

/** The newest uncompressed .glb of a variant's asset row — the model the editor edits. */
export const latestOriginalGlb = (row) =>
    (row?.assets || [])
        .filter((a) => a.kind === 'original' && a.assetFormat === 'glb')
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0] || null;

/** An unlit arrow mesh (shaft + cone) pointing along `dir` from `origin`. Not pickable, not exported. */
const makeArrow = (scene, name, origin, dir, length, color) => {
    const mat = new StandardMaterial(`${name}-mat`, scene);
    mat.emissiveColor = color;
    mat.disableLighting = true;
    const shaft = MeshBuilder.CreateCylinder(`${name}-shaft`, { height: length * 0.72, diameter: length * 0.05 }, scene);
    shaft.position.y = length * 0.36;
    const head = MeshBuilder.CreateCylinder(
        `${name}-head`,
        { height: length * 0.28, diameterBottom: length * 0.14, diameterTop: 0 },
        scene,
    );
    head.position.y = length * 0.86;
    const root = new TransformNode(name, scene);
    [shaft, head].forEach((m) => {
        m.parent = root;
        m.material = mat;
        m.isPickable = false;
    });
    root.position.copyFrom(origin);
    const d = dir.normalizeToNew();
    if (d.y < -0.999) {
        root.rotationQuaternion = Quaternion.RotationAxis(Vector3.Right(), Math.PI);
    } else if (d.y < 0.999) {
        const axis = Vector3.Cross(Vector3.Up(), d).normalize();
        root.rotationQuaternion = Quaternion.RotationAxis(axis, Math.acos(Vector3.Dot(Vector3.Up(), d)));
    }
    return root;
};

/** A camera-facing text label floating at `position`. Not pickable, not exported. */
const makeLabel = (scene, text, position, size, color) => {
    const tex = new DynamicTexture(`label-${text}`, { width: 256, height: 96 }, scene, false);
    tex.hasAlpha = true;
    tex.drawText(text, null, 68, 'bold 64px sans-serif', color, 'transparent', true);
    const mat = new StandardMaterial(`label-${text}-mat`, scene);
    mat.emissiveTexture = tex;
    mat.opacityTexture = tex;
    mat.disableLighting = true;
    mat.backFaceCulling = false;
    const plane = MeshBuilder.CreatePlane(`label-${text}`, { width: size, height: size * 0.375 }, scene);
    plane.billboardMode = Mesh.BILLBOARDMODE_ALL;
    plane.position.copyFrom(position);
    plane.material = mat;
    plane.isPickable = false;
    return plane;
};

const DimCell = ({ label, value }) => (
    <div style={{ textAlign: 'center', padding: '4px 12px', border: '1px solid #f0f0f0', borderRadius: 6 }}>
        <div style={{ fontSize: 11, color: '#888' }}>{label}</div>
        <div style={{ fontSize: 14 }}>{fmt(value)}</div>
    </div>
);

/**
 * Per-item model editor: one Babylon engine for the whole item, every variant's
 * latest original .glb pre-loaded into the same scene (hidden), and a top menu
 * to pick which variant's model is shown and edited.
 */
const EditorBody = ({ item, assetsByVariant, onSaved }) => {
    const canvasRef = useRef(null);
    const sceneRef = useRef(null);
    const cameraRef = useRef(null);
    const gizmoRef = useRef(null);
    const editMatRef = useRef(null);
    const selectedMatRef = useRef(null);
    // variantId -> { assetId, root, exportNodes, modelMeshes, planeMeshes: Map(id -> mesh), planeSeed,
    //                modelBedMeshes: mattress/blanket meshes the uploaded model came with (exported verbatim),
    //                bed: null | { group, mattressNode, blanketNode, blanketOffset, spec,
    //                              parts: { mattress, blanket?, blanket_container? } } (the editable bedding) }
    const modelsRef = useRef(new Map());
    // { mattress, mattressSize, blankets: { styleId -> { blanket, container?, mattressSize } } } once loaded
    const bedTemplatesRef = useRef(null);
    const bedPartsPromiseRef = useRef(null);
    const pendingRef = useRef(new Map()); // variantId -> assetId being fetched/imported
    const guidesRef = useRef([]); // world-space UP/FRONT arrows + labels, rebuilt per active variant
    const bbRef = useRef(null);
    const stepRef = useRef(0.05);
    const seqRef = useRef(0); // unique plane ids
    const activeIdRef = useRef(null);
    const selectedIdRef = useRef(null);
    const framedKeyRef = useRef(null);
    const disposedRef = useRef(false);

    const [engineReady, setEngineReady] = useState(false);
    const [modelStates, setModelStates] = useState({}); // variantId -> { status: 'loading'|'ready'|'error', error }
    const [activeId, setActiveId] = useState(null);
    const [dims, setDims] = useState(null);
    const [planesByVariant, setPlanesByVariant] = useState({}); // variantId -> [{ id, name, width, depth, mode }]
    // variantId -> null | { kind: 'model' } (bedding came with the model) |
    //   { kind: 'editable', width, depth, height, blanket: styleId | null, mode, blanketMode } (the default parts)
    const [bedByVariant, setBedByVariant] = useState({});
    const [bedTemplatesState, setBedTemplatesState] = useState('idle'); // idle | loading | ready | error
    const [selectedId, setSelectedId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [savingAll, setSavingAll] = useState(false);

    const planes = planesByVariant[activeId] || [];
    const bed = bedByVariant[activeId] || null;
    const isBed = isBedType(item.itemType);
    const blanketSeparate = isSeparateBlanket(bed?.blanket);
    const blanketHasContainer = Boolean(bedTemplatesRef.current?.blankets[bed?.blanket]?.container);
    const menuVariants = sortedVariants(item).filter((v) => latestOriginalGlb(assetsByVariant[v.id]));
    const otherVariants = menuVariants.filter((v) => v.id !== activeId);
    const activeReady = modelStates[activeId]?.status === 'ready';

    useEffect(() => {
        activeIdRef.current = activeId;
    }, [activeId]);

    // Sync bbRef/stepRef/dims to the active model's current world bounds (changes when it's rotated).
    const refreshBounds = () => {
        const entry = modelsRef.current.get(activeIdRef.current);
        const wb = entry && worldBounds(entry.modelMeshes);
        if (!wb) {
            bbRef.current = null;
            setDims(null);
            return;
        }
        const width = wb.max.x - wb.min.x;
        const height = wb.max.y - wb.min.y;
        const depth = wb.max.z - wb.min.z;
        const maxDim = Math.max(width, height, depth) || 1;
        bbRef.current = {
            min: wb.min,
            max: wb.max,
            center: wb.min.add(wb.max).scale(0.5),
            width,
            height,
            depth,
            maxDim,
        };
        stepRef.current = maxDim / 100;
        setDims({ width, height, depth });
    };

    // World-space orientation guides beside the active model: how it will stand
    // in the room editor. They never rotate with the model — the goal is to
    // rotate the model until it agrees with them.
    const rebuildGuides = () => {
        const scene = sceneRef.current;
        guidesRef.current.forEach((n) => n.dispose(false, true));
        guidesRef.current = [];
        const bb = bbRef.current;
        if (!scene || !bb) return;
        const len = bb.maxDim * 0.45;
        const anchor = new Vector3(bb.max.x + bb.maxDim * 0.18, bb.min.y, bb.center.z);
        guidesRef.current.push(makeArrow(scene, 'up-arrow', anchor, Vector3.Up(), len, new Color3(0.15, 0.8, 0.3)));
        guidesRef.current.push(makeLabel(scene, 'UP', anchor.add(new Vector3(0, len * 1.2, 0)), len * 0.55, '#4ade80'));
        const front = frontDirectionOf(item.itemType);
        guidesRef.current.push(makeArrow(scene, 'front-arrow', anchor, front, len, new Color3(1, 0.55, 0.1)));
        guidesRef.current.push(makeLabel(scene, 'FRONT', anchor.add(front.scale(len * 1.2)), len * 0.65, '#fb923c'));
    };

    const resetRotation = () => {
        const root = modelsRef.current.get(activeId)?.root;
        if (!root) return;
        root.rotationQuaternion = Quaternion.Identity();
        refreshBounds();
    };

    // (Re)create a plane mesh for `id` on `entry` at the given footprint + position, lying flat.
    const makePlane = (entry, id, width, depth, position) => {
        const scene = sceneRef.current;
        const existing = entry.planeMeshes.get(id);
        if (existing) existing.dispose();
        const plane = MeshBuilder.CreatePlane(
            'container-plane',
            { width: Math.max(width, 0.01), height: Math.max(depth, 0.01), sideOrientation: Mesh.DOUBLESIDE },
            scene,
        );
        plane.rotation.x = Math.PI / 2; // lie flat in XZ, normal +Y
        plane.position.copyFrom(position);
        plane.isPickable = true;
        plane.material = editMatRef.current;
        plane.metadata = { planeId: id };
        entry.planeMeshes.set(id, plane);
        return plane;
    };

    // Materials stay: the parts share the template materials (and the shared edit material).
    const disposeBed = (entry) => {
        entry.bed?.group.dispose(false, false);
        entry.bed = null;
    };

    // Size the normalized parts to the spec: the mattress (1 long, resting on y=0)
    // stretches to width/depth/height; the blanket covers this mattress the way it
    // covered the mattress it was modeled on (whatever bed type that was), scales
    // its own height with the footprint (not the mattress thickness), and rides on
    // the mattress top plus the admin's nudge.
    const applyBedSpec = (entry, spec) => {
        const { mattressSize, blankets } = bedTemplatesRef.current;
        const { mattressNode, blanketNode, blanketOffset, parts } = entry.bed;
        const width = Math.max(spec.width, 0.01);
        const depth = Math.max(spec.depth, 0.01);
        const height = Math.max(spec.height, 0.01);
        mattressNode.scaling = new Vector3(width / mattressSize.x, height / mattressSize.y, depth / mattressSize.z);
        if (blanketNode) {
            const source = blankets[spec.blanket].mattressSize;
            const sx = width / source.x;
            const sz = depth / source.z;
            blanketNode.scaling = new Vector3(sx, (sx + sz) / 2, sz);
            blanketNode.position = new Vector3(blanketOffset.x, height + blanketOffset.y, blanketOffset.z);
        }
        entry.bed.spec = spec;
        return parts;
    };

    // (Re)create the editable bedding on `entry`: clones of the type's mattress and
    // the chosen blanket style under one world-space pivot at the mattress's bottom center, already carrying
    // their catalog mesh names. `blanketOffset` is the blanket's nudge off its default
    // spot; `rotation` the gizmo's turn of the whole bedding.
    const makeBed = (entry, spec, position, blanketOffset = Vector3.Zero(), rotation = Quaternion.Identity()) => {
        const scene = sceneRef.current;
        disposeBed(entry);
        const { mattress, blankets } = bedTemplatesRef.current;
        const style = spec.blanket ? blankets[spec.blanket] : null;
        const group = new TransformNode('bed-parts', scene);
        group.position.copyFrom(position);
        group.rotationQuaternion = rotation.clone();
        const mattressNode = new TransformNode('bed-mattress', scene);
        mattressNode.parent = group;
        const blanketNode = style ? new TransformNode('bed-blanket', scene) : null;
        if (blanketNode) blanketNode.parent = group;
        const clonePart = (template, name, parent, planeId) => {
            if (!template) return undefined;
            const mesh = template.clone(name, parent);
            mesh.setEnabled(true);
            mesh.isPickable = true;
            mesh.metadata = { planeId };
            mesh.outlineColor = BED_OUTLINE_COLOR;
            return mesh;
        };
        // Clicking a draped blanket selects the mattress it lies on; only a
        // separately placed blanket (rail-hung) is its own selection.
        const blanketId = isSeparateBlanket(spec.blanket) ? BLANKET_ID : BED_ID;
        const parts = {
            mattress: clonePart(mattress, MATTRESS_MESH_NAME, mattressNode, BED_ID),
            blanket: clonePart(style?.blanket, BLANKET_MESH_NAME, blanketNode, blanketId),
            blanket_container: clonePart(style?.container, BLANKET_CONTAINER_MESH_NAME, blanketNode, blanketId),
        };
        if (parts.blanket_container) parts.blanket_container.material = editMatRef.current;
        entry.bed = { group, mattressNode, blanketNode, blanketOffset: blanketOffset.clone(), parts };
        applyBedSpec(entry, spec);
        return entry.bed;
    };

    const disposeVariant = (variantId) => {
        const entry = modelsRef.current.get(variantId);
        if (!entry) return;
        entry.planeMeshes.forEach((m) => m.dispose());
        disposeBed(entry);
        entry.root?.dispose(false, true);
        modelsRef.current.delete(variantId);
    };

    // Engine + scene are created once per item, as soon as the assets step mounts,
    // so the editor opens instantly with the models already loaded.
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return undefined;
        disposedRef.current = false;
        const { engine, scene, camera } = setupScene(canvas);
        sceneRef.current = scene;
        cameraRef.current = camera;
        const onResize = () => engine.resize();
        window.addEventListener('resize', onResize);
        // The canvas fills a flex pane inside an animating Drawer — window resize
        // alone misses the drawer's open animation and pane layout changes.
        const resizeObserver = new ResizeObserver(onResize);
        resizeObserver.observe(canvas);

        const editMat = new StandardMaterial('plane-edit', scene);
        editMat.diffuseColor = new Color3(0.1, 0.5, 1);
        editMat.emissiveColor = new Color3(0.1, 0.45, 0.95);
        editMat.alpha = 0.35;
        editMat.backFaceCulling = false;
        editMat.disableLighting = true;
        editMatRef.current = editMat;

        const selMat = new StandardMaterial('plane-selected', scene);
        selMat.diffuseColor = new Color3(1, 0.2, 0.2);
        selMat.emissiveColor = new Color3(0.9, 0.1, 0.1);
        selMat.alpha = 0.55;
        selMat.backFaceCulling = false;
        selMat.disableLighting = true;
        selectedMatRef.current = selMat;

        // Click a plane to select it (list rows also select). A tap on the model or
        // empty space deselects. Tap = pointerup within a few px of pointerdown so
        // camera-drags don't change selection.
        let downX = 0;
        let downY = 0;
        const onPointerDown = (e) => {
            downX = e.clientX;
            downY = e.clientY;
        };
        const onPointerUp = (e) => {
            if (Math.abs(e.clientX - downX) > 4 || Math.abs(e.clientY - downY) > 4) return;
            const rect = canvas.getBoundingClientRect();
            const pick = scene.pick(e.clientX - rect.left, e.clientY - rect.top);
            setSelectedId(pick?.hit ? pick.pickedMesh?.metadata?.planeId ?? null : null);
        };
        canvas.addEventListener('pointerdown', onPointerDown);
        canvas.addEventListener('pointerup', onPointerUp);

        const utilLayer = new UtilityLayerRenderer(scene);
        const gizmo = new RotationGizmo(utilLayer);
        gizmo.updateGizmoRotationToMatchAttachedMesh = false; // world-aligned rings
        // Snap to 15° — a hand-rotated model that's even ~2° off vertical bakes a
        // tilted bbox whose pivot puts the item inside the editor's wall (collision).
        gizmo.snapDistance = Math.PI / 12;
        [gizmo.xGizmo, gizmo.yGizmo, gizmo.zGizmo].forEach((g) =>
            g.dragBehavior.onDragEndObservable.add(() => refreshBounds()),
        );
        gizmoRef.current = gizmo;

        if (isBedType(item.itemType)) {
            setBedTemplatesState('loading');
            bedPartsPromiseRef.current = loadBedParts(scene, item.itemType);
            bedPartsPromiseRef.current.then(
                (loaded) => {
                    if (disposedRef.current) return;
                    bedTemplatesRef.current = loaded;
                    setBedTemplatesState('ready');
                },
                (e) => {
                    if (disposedRef.current) return;
                    setBedTemplatesState('error');
                    message.error(e.message || 'Failed to load the default mattress and blanket');
                },
            );
        }

        setEngineReady(true);

        return () => {
            disposedRef.current = true;
            window.removeEventListener('resize', onResize);
            resizeObserver.disconnect();
            canvas.removeEventListener('pointerdown', onPointerDown);
            canvas.removeEventListener('pointerup', onPointerUp);
            gizmo.dispose();
            utilLayer.dispose();
            engine.dispose();
            modelsRef.current.clear();
            pendingRef.current.clear();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadVariant = async (variantId, asset) => {
        const scene = sceneRef.current;
        pendingRef.current.set(variantId, asset.id);
        setModelStates((s) => ({ ...s, [variantId]: { status: 'loading' } }));
        try {
            // Saved bedding is rebuilt from the bed parts, so they have to be in first.
            await bedPartsPromiseRef.current?.catch(() => null);
            const blob = await ingestionApi.getAssetBlob(item.id, asset.id);
            if (disposedRef.current || pendingRef.current.get(variantId) !== asset.id) return;
            const result = await loadModelFromBlob(scene, blob, '.glb');
            if (disposedRef.current || pendingRef.current.get(variantId) !== asset.id) {
                result.meshes.forEach((m) => !m.isDisposed() && m.dispose(false, true));
                (result.transformNodes || []).forEach((n) => n.dispose());
                return;
            }
            disposeVariant(variantId);

            // Bedding this editor saved comes back editable: its meshes are dropped
            // here and rebuilt from the saved settings below.
            const savedBedding = bedTemplatesRef.current ? savedBeddingOf(result.meshes) : null;
            if (savedBedding) {
                let top = savedBedding.mesh;
                while (top.parent?.parent) top = top.parent;
                top.dispose(false, true);
                result.meshes.filter((m) => !m.isDisposed() && isBedPartMeshName(m.name)).forEach((m) => m.dispose(false, true));
            }
            const loadedMeshes = result.meshes.filter((m) => !m.isDisposed());
            const loadedTransformNodes = (result.transformNodes || []).filter((n) => !n.isDisposed());

            // Any other bedding the model carries (mattress / blanket meshes) is kept
            // verbatim until the admin replaces it with the editable defaults; container
            // meshes are surfaced as editable planes below; the rest is the model we
            // frame, size against, and export.
            const modelBedMeshes = loadedMeshes.filter((m) => isBedPartMeshName(m.name));
            const containerMeshes = loadedMeshes.filter((m) => !isBedPartMeshName(m.name) && isContainerMeshName(m.name));
            const modelMeshes = loadedMeshes.filter((m) => !isBedPartMeshName(m.name) && !isContainerMeshName(m.name));
            const framed = modelMeshes.length ? modelMeshes : loadedMeshes;

            const nodes = new Set();
            const addChain = (node) => {
                let n = node;
                while (n) {
                    nodes.add(n);
                    n = n.parent;
                }
            };
            modelMeshes.forEach(addChain);
            modelBedMeshes.forEach(addChain);
            loadedTransformNodes.forEach(addChain);

            // Hang the whole model under one pivot at its bbox center so the
            // rotation gizmo spins it in place. The pivot is included in the
            // export, so the rotation is baked into the saved .glb (the client
            // re-pivots by bounding box on load, so any offset is neutralized).
            const wb = worldBounds(framed);
            const root = new TransformNode('model-orientation', scene);
            // Without a quaternion the gizmo writes Euler angles into `.rotation`
            // instead — the export of THIS variant still bakes them (the exporter
            // falls back to Euler), but "Save + apply" reads rotationQuaternion
            // and would silently apply an identity rotation to the other variants.
            root.rotationQuaternion = Quaternion.Identity();
            if (wb) root.position = wb.min.add(wb.max).scale(0.5);
            [...loadedMeshes, ...loadedTransformNodes]
                .filter((n) => !n.parent)
                .forEach((n) => n.setParent(root));
            nodes.add(root);

            const entry = {
                assetId: asset.id,
                root,
                exportNodes: nodes,
                modelMeshes,
                modelBedMeshes,
                planeMeshes: new Map(),
                planeSeed: 0,
                bed: null,
            };
            modelsRef.current.set(variantId, entry);

            // Rebuild each existing container as a flat editor plane (from its world
            // bbox) so it can be re-sized, moved, or deleted; this tool only authors
            // horizontal planes, so the original mesh is replaced.
            let maxIdx = -1;
            const seeded = [];
            for (const mesh of containerMeshes) {
                const name = mesh.name;
                // Bound the node together with its descendant meshes — a container
                // can be a parent transform whose geometry lives on children.
                const children = mesh.getChildMeshes ? mesh.getChildMeshes() : [];
                const b = worldBounds([mesh, ...children]);
                if (!b) {
                    // Can't reconstruct it as a flat plane — keep the original mesh in
                    // the export so re-saving doesn't silently drop the container.
                    addChain(mesh);
                    continue;
                }
                mesh.dispose();
                const width = round(b.max.x - b.min.x);
                const depth = round(b.max.z - b.min.z);
                const id = `p${seqRef.current++}`;
                makePlane(entry, id, Math.max(width, 0.01), Math.max(depth, 0.01), b.min.add(b.max).scale(0.5));
                const idxMatch = /_(\d+)$/.exec(name);
                if (idxMatch) {
                    maxIdx = Math.max(maxIdx, Number(idxMatch[1]));
                }
                seeded.push({ id, name, width, depth, mode: 'xz' });
            }
            entry.planeSeed = maxIdx + 1;

            // Added hidden — the menu-selection effect shows the active one.
            root.setEnabled(false);
            entry.planeMeshes.forEach((m) => m.setEnabled(false));
            if (savedBedding) {
                const { spec, position, blanketOffset, rotation } = savedBedding;
                makeBed(entry, spec, position, blanketOffset, rotation).group.setEnabled(false);
            }

            setPlanesByVariant((prev) => ({ ...prev, [variantId]: seeded }));
            setBedByVariant((prev) => ({
                ...prev,
                [variantId]: savedBedding
                    ? { kind: 'editable', ...savedBedding.spec }
                    : modelBedMeshes.length
                      ? { kind: 'model' }
                      : null,
            }));
            if (activeIdRef.current === variantId) setSelectedId(null);
            setModelStates((s) => ({ ...s, [variantId]: { status: 'ready' } }));
            setActiveId((cur) => cur ?? variantId);
        } catch (e) {
            if (!disposedRef.current) {
                setModelStates((s) => ({
                    ...s,
                    [variantId]: { status: 'error', error: e.message || 'Failed to load model' },
                }));
            }
        } finally {
            if (pendingRef.current.get(variantId) === asset.id) pendingRef.current.delete(variantId);
        }
    };

    // Load (and reload after regenerate/upload) every variant's latest original .glb
    // as soon as the assets list is known — before the drawer is ever opened.
    useEffect(() => {
        if (!engineReady) return;
        (item.variants || []).forEach((variant) => {
            const target = latestOriginalGlb(assetsByVariant[variant.id]);
            const entry = modelsRef.current.get(variant.id);
            if (!target) {
                if (entry) {
                    disposeVariant(variant.id);
                    pendingRef.current.delete(variant.id);
                    setPlanesByVariant((prev) => ({ ...prev, [variant.id]: [] }));
                    setBedByVariant((prev) => ({ ...prev, [variant.id]: null }));
                    setModelStates((s) => {
                        const { [variant.id]: _, ...rest } = s;
                        return rest;
                    });
                    setActiveId((cur) => (cur === variant.id ? null : cur));
                }
                return;
            }
            if (entry?.assetId === target.id || pendingRef.current.get(variant.id) === target.id) return;
            loadVariant(variant.id, target);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [engineReady, assetsByVariant, item.variants]);

    // Show the active variant's model + planes, hide all the others, and retarget
    // the gizmo/camera/guides. Also re-runs when a model finishes (re)loading.
    useEffect(() => {
        if (!engineReady) return;
        modelsRef.current.forEach((entry, vid) => {
            const on = vid === activeId;
            entry.root.setEnabled(on);
            entry.planeMeshes.forEach((m) => m.setEnabled(on));
            entry.bed?.group.setEnabled(on);
        });
        const entry = modelsRef.current.get(activeId);
        retargetGizmo();
        refreshBounds();
        const framedKey = entry ? `${activeId}:${entry.assetId}` : null;
        if (framedKey && framedKey !== framedKeyRef.current) {
            framedKeyRef.current = framedKey;
            rebuildGuides();
            frameCamera(cameraRef.current, entry.modelMeshes.length ? entry.modelMeshes : [], item.itemType);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [engineReady, activeId, modelStates]);

    useEffect(() => {
        setSelectedId(null);
    }, [activeId]);

    // The rotation gizmo turns the selected mattress (the whole bedding turns with
    // it); with nothing selected it turns the model, as before.
    const retargetGizmo = () => {
        const entry = modelsRef.current.get(activeIdRef.current);
        if (!gizmoRef.current) return;
        gizmoRef.current.attachedNode = (selectedIdRef.current === BED_ID && entry?.bed?.group) || entry?.root || null;
    };
    useEffect(() => {
        selectedIdRef.current = selectedId;
        retargetGizmo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId, bed]);

    // Reflect selection: planes swap material (selected = red, others = translucent
    // blue); the bedding keeps its white material and gets a red outline instead.
    useEffect(() => {
        const entry = modelsRef.current.get(activeId);
        entry?.planeMeshes.forEach((mesh, id) => {
            mesh.material = id === selectedId ? selectedMatRef.current : editMatRef.current;
        });
        if (entry?.bed) {
            const { parts } = entry.bed;
            const width = (bbRef.current?.maxDim || 1) * 0.004;
            // Babylon offsets the outline in the mesh's LOCAL space, before the node's
            // scale: the parts are normalized (1 unit long) and stretched to the bed, so
            // a width in model units has to be divided by that stretch or a 72" mattress
            // wears a 20" red shell (seen on inch-scale builder models, 2026-11-04).
            const outline = (mesh, on) => {
                if (!mesh) return;
                mesh.computeWorldMatrix(true);
                const s = mesh.absoluteScaling;
                mesh.renderOutline = on;
                mesh.outlineWidth = width / Math.max(Math.abs(s.x), Math.abs(s.y), Math.abs(s.z), 1e-6);
            };
            const blanketOn = selectedId === BLANKET_ID || (selectedId === BED_ID && !blanketSeparate);
            outline(parts.mattress, selectedId === BED_ID);
            outline(parts.blanket, blanketOn);
            if (parts.blanket_container) {
                parts.blanket_container.material = blanketOn ? selectedMatRef.current : editMatRef.current;
            }
        }
    }, [selectedId, planes, bed, activeId]);

    const selectedMode =
        selectedId === BED_ID
            ? bed?.mode || 'xz'
            : selectedId === BLANKET_ID
              ? bed?.blanketMode || 'xz'
              : planes.find((p) => p.id === selectedId)?.mode || 'xz';

    // Arrow keys nudge the selected plane. In 'y' mode ↑/↓ change height; in 'xz'
    // mode movement is camera-relative: ↑ pushes the plane away from the camera, ↓
    // toward it, →/← along the camera's right/left (projected onto the ground plane).
    useEffect(() => {
        if (!selectedId) return undefined;
        const onKey = (e) => {
            const tag = document.activeElement?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA') return;
            const entry = modelsRef.current.get(activeId);
            const scene = sceneRef.current;
            // The blanket nudge is stored as an offset and re-applied (its rest height
            // follows the mattress thickness); everything else moves its node directly.
            const isBlanket = selectedId === BLANKET_ID;
            const mesh = isBlanket
                ? entry?.bed?.blanketNode && { position: entry.bed.blanketOffset }
                : selectedId === BED_ID
                  ? entry?.bed?.group
                  : entry?.planeMeshes.get(selectedId);
            if (!mesh || !scene) return;
            const s = stepRef.current;
            const applied = () => {
                if (isBlanket && bed) applyBedSpec(entry, bed);
            };

            if (selectedMode === 'y') {
                let handled = true;
                if (e.key === 'ArrowUp') mesh.position.y += s;
                else if (e.key === 'ArrowDown') mesh.position.y -= s;
                else handled = false;
                if (handled) {
                    e.preventDefault();
                    applied();
                }
                return;
            }

            const cam = scene.activeCamera;
            const fwd = cam.getDirection(Vector3.Forward());
            const right = cam.getDirection(Vector3.Right());
            fwd.y = 0;
            right.y = 0;
            if (fwd.lengthSquared() > 1e-6) fwd.normalize();
            if (right.lengthSquared() > 1e-6) right.normalize();
            let handled = true;
            switch (e.key) {
                case 'ArrowUp':
                    mesh.position.addInPlace(fwd.scale(s));
                    break;
                case 'ArrowDown':
                    mesh.position.addInPlace(fwd.scale(-s));
                    break;
                case 'ArrowRight':
                    mesh.position.addInPlace(right.scale(s));
                    break;
                case 'ArrowLeft':
                    mesh.position.addInPlace(right.scale(-s));
                    break;
                default:
                    handled = false;
            }
            if (handled) {
                e.preventDefault();
                applied();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId, selectedMode, activeId, bed]);

    const setActivePlanes = (updater) =>
        setPlanesByVariant((prev) => ({ ...prev, [activeId]: updater(prev[activeId] || []) }));

    const addPlane = () => {
        const entry = modelsRef.current.get(activeId);
        const bb = bbRef.current;
        if (!entry || !bb) return;
        const id = `p${seqRef.current++}`;
        const name = `${DEFAULT_PLANE_NAME}_${entry.planeSeed++}`;
        const width = round(bb.width);
        const depth = round(bb.depth);
        const pos = new Vector3(bb.center.x, bb.max.y + bb.maxDim * 0.05, bb.center.z);
        makePlane(entry, id, width, depth, pos);
        setActivePlanes((prev) => [...prev, { id, name, width, depth, mode: 'xz' }]);
        setSelectedId(id);
    };

    const updatePlane = (id, patch) => {
        setActivePlanes((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
        if (patch.width !== undefined || patch.depth !== undefined) {
            const entry = modelsRef.current.get(activeId);
            const mesh = entry?.planeMeshes.get(id);
            const cur = planes.find((p) => p.id === id);
            if (mesh && cur) {
                const pos = mesh.position.clone();
                makePlane(entry, id, patch.width ?? cur.width, patch.depth ?? cur.depth, pos);
            }
        }
    };

    const removePlane = (id) => {
        const entry = modelsRef.current.get(activeId);
        entry?.planeMeshes.get(id)?.dispose();
        entry?.planeMeshes.delete(id);
        setActivePlanes((prev) => prev.filter((p) => p.id !== id));
        setSelectedId((cur) => (cur === id ? null : cur));
    };

    const setActiveBed = (value) => setBedByVariant((prev) => ({ ...prev, [activeId]: value }));

    const addBed = () => {
        const entry = modelsRef.current.get(activeId);
        const bb = bbRef.current;
        if (!entry || !bb || !bedTemplatesRef.current) return;
        const spec = defaultBedSpec(bb, item.itemType, bedTemplatesRef.current);
        makeBed(entry, spec, defaultBedPosition(bb, item.itemType, spec));
        setActiveBed({ kind: 'editable', ...spec });
        setSelectedId(BED_ID);
    };

    const updateBed = (patch) => {
        const entry = modelsRef.current.get(activeId);
        if (!entry?.bed || bed?.kind !== 'editable') return;
        const next = { ...bed, ...patch };
        if (next.blanket !== bed.blanket) {
            // Another blanket is other meshes: rebuild in place, back on its default spot.
            const { group } = entry.bed;
            makeBed(entry, next, group.position.clone(), Vector3.Zero(), group.rotationQuaternion.clone());
        } else {
            applyBedSpec(entry, next);
        }
        setActiveBed(next);
    };

    const removeBed = () => {
        const entry = modelsRef.current.get(activeId);
        if (entry) disposeBed(entry);
        setActiveBed(null);
        setSelectedId((cur) => (cur === BED_ID || cur === BLANKET_ID ? null : cur));
    };

    // Swap the bedding the model shipped with for the editable defaults, sized and
    // placed from the model's own mattress. The original meshes leave the export.
    const replaceModelBed = () => {
        const entry = modelsRef.current.get(activeId);
        if (!entry || !bedTemplatesRef.current) return;
        const mattress = entry.modelBedMeshes.filter((m) => isMattressMeshName(m.name));
        const blanket = entry.modelBedMeshes.some((m) => isBlanketMeshName(m.name) && !m.name.includes(INVISIBLE_MARKER));
        const b = worldBounds(mattress);
        const bb = bbRef.current;
        const spec = b
            ? {
                  width: round(b.max.x - b.min.x),
                  depth: round(b.max.z - b.min.z),
                  height: round(b.max.y - b.min.y),
                  blanket: blanket ? defaultBlanketStyle(item.itemType) ?? BLANKET_STYLES[0].id : null,
                  mode: 'xz',
                  blanketMode: 'xz',
              }
            : defaultBedSpec(bb, item.itemType, bedTemplatesRef.current);
        const position = b
            ? new Vector3((b.min.x + b.max.x) / 2, b.min.y, (b.min.z + b.max.z) / 2)
            : defaultBedPosition(bb, item.itemType, spec);
        entry.modelBedMeshes.forEach((m) => {
            entry.exportNodes.delete(m);
            m.dispose();
        });
        entry.modelBedMeshes = [];
        makeBed(entry, spec, position);
        setActiveBed({ kind: 'editable', ...spec });
        setSelectedId(BED_ID);
    };

    // Renames + hides the variant's planes and exports just that variant's subtree
    // (its model nodes + planes) out of the shared scene. Restores the edit
    // materials afterwards — the editor stays open across saves.
    const exportVariantGlb = async (variantId, planeList) => {
        const scene = sceneRef.current;
        const entry = modelsRef.current.get(variantId);
        const saveMat = new StandardMaterial('plane-save', scene);
        saveMat.alpha = 0;
        saveMat.transparencyMode = Material.MATERIAL_ALPHABLEND;
        try {
            planeList.forEach((p) => {
                const mesh = entry.planeMeshes.get(p.id);
                if (mesh) {
                    mesh.name = p.name || DEFAULT_PLANE_NAME;
                    mesh.material = saveMat;
                }
            });
            const planeMeshes = new Set(entry.planeMeshes.values());
            // The bedding exports as-is (white mattress + blanket, already named for the
            // catalog) plus its invisible container plane, and carries its settings so
            // the next load can rebuild it editable.
            const bedNodes = new Set();
            if (entry.bed) {
                const { group, mattressNode, blanketNode, parts } = entry.bed;
                bedNodes.add(group);
                bedNodes.add(mattressNode);
                bedNodes.add(parts.mattress);
                parts.mattress.metadata = {
                    ...parts.mattress.metadata,
                    gltf: { extras: { [BEDDING_EXTRAS_KEY]: beddingExtras(entry.bed) } },
                };
                if (blanketNode) {
                    bedNodes.add(blanketNode);
                    [parts.blanket, parts.blanket_container].filter(Boolean).forEach((mesh) => bedNodes.add(mesh));
                }
                Object.values(parts).forEach((mesh) => {
                    if (mesh) mesh.renderOutline = false;
                });
                if (parts.blanket_container) parts.blanket_container.material = saveMat;
            }
            await scene.whenReadyAsync();
            const data = await GLTF2Export.GLBAsync(scene, 'model', {
                shouldExportNode: (node) => planeMeshes.has(node) || bedNodes.has(node) || entry.exportNodes.has(node),
                // The exporter drops the UVs of untextured meshes by default; the
                // white mattress/blanket get their texture in the room editor, on
                // exactly these UVs.
                exportUnusedUVs: true,
            });
            return new File([data.glTFFiles['model.glb']], 'model.glb', { type: 'model/gltf-binary' });
        } finally {
            const selectedHere = variantId === activeId;
            entry.planeMeshes.forEach((mesh, id) => {
                mesh.material = selectedHere && id === selectedId ? selectedMatRef.current : editMatRef.current;
            });
            if (entry.bed) {
                const { parts } = entry.bed;
                const blanketOn = selectedHere && (selectedId === BLANKET_ID || (selectedId === BED_ID && !blanketSeparate));
                parts.mattress.renderOutline = selectedHere && selectedId === BED_ID;
                if (parts.blanket) parts.blanket.renderOutline = blanketOn;
                if (parts.blanket_container) {
                    parts.blanket_container.material = blanketOn ? selectedMatRef.current : editMatRef.current;
                }
            }
            saveMat.dispose();
        }
    };

    const uploadVariantGlb = async (variantId, file) => {
        const saved = await ingestionApi.uploadAsset(item.id, variantId, file);
        // Remember the uploaded asset as the loaded one so the assets refresh
        // doesn't re-download a model that's already in the scene.
        const entry = modelsRef.current.get(variantId);
        if (entry && saved?.id) entry.assetId = saved.id;
    };

    const saveVariant = async (variantId, planeList) => {
        const file = await exportVariantGlb(variantId, planeList);
        await uploadVariantGlb(variantId, file);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await saveVariant(activeId, planes);
            const saved = [];
            if (bed?.kind === 'editable') saved.push(bed.blanket ? 'the mattress + blanket' : 'the mattress');
            if (planes.length) saved.push(`${planes.length} container plane${planes.length > 1 ? 's' : ''}`);
            message.success(saved.length ? `Saved a new .glb with ${saved.join(' and ')}` : 'Saved a new .glb with the updated orientation');
            onSaved?.();
        } catch (e) {
            message.error(e.message || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    const SAVE_ALL_KEY = 'save-all-variants';

    // Save the active variant, then apply its rotation to every other loaded
    // variant (visibly, in the scene) and its planes to the ones sharing this
    // variant's geometry, re-exporting + uploading each affected model. Exports
    // run one at a time (they mutate the shared scene), but each upload starts
    // the moment its file is ready and they all run in parallel — the uploads
    // are network-bound and dominate the total time.
    const handleSaveAll = async () => {
        setSavingAll(true);
        try {
            const activeVariant = (item.variants || []).find((v) => v.id === activeId);
            const groupKey = activeVariant ? geometryGroupKey(item, activeVariant) : null;
            const activeEntry = modelsRef.current.get(activeId);
            const activeRoot = activeEntry.root;
            const rotationQ =
                activeRoot.rotationQuaternion?.clone() ||
                Quaternion.FromEulerAngles(activeRoot.rotation.x, activeRoot.rotation.y, activeRoot.rotation.z);
            const rotationChanged = !rotationQ.equalsWithEpsilon(Quaternion.Identity(), 1e-4);
            // The editable bedding transfers to same-geometry variants like the planes do.
            const bedSpec =
                bed?.kind === 'editable' && activeEntry.bed
                    ? {
                          ...bed,
                          position: activeEntry.bed.group.position.clone(),
                          blanketOffset: activeEntry.bed.blanketOffset.clone(),
                          rotation: activeEntry.bed.group.rotationQuaternion.clone(),
                      }
                    : null;
            // Capture the plane geometry before saving mutates names.
            const planeSpecs = planes
                .map((p) => {
                    const mesh = activeEntry.planeMeshes.get(p.id);
                    return (
                        mesh && {
                            name: p.name || DEFAULT_PLANE_NAME,
                            width: p.width,
                            depth: p.depth,
                            position: mesh.position.clone(),
                        }
                    );
                })
                .filter(Boolean);

            const skipped = [];
            const uploads = [];
            const startUpload = (variantId, label, file) =>
                uploads.push(
                    uploadVariantGlb(variantId, file).then(
                        () => ({ label, ok: true }),
                        () => ({ label, ok: false }),
                    ),
                );

            let exported = 0;
            const progress = (label) =>
                message.loading({
                    content: `Exporting ${label} (${++exported}/${otherVariants.length + 1})…`,
                    key: SAVE_ALL_KEY,
                    duration: 0,
                });

            progress('this variant');
            startUpload(activeId, 'this variant', await exportVariantGlb(activeId, planes));

            for (const v of otherVariants) {
                const label = variationLabel(item, v);
                const entry = modelsRef.current.get(v.id);
                if (!entry || modelStates[v.id]?.status !== 'ready') {
                    skipped.push(`${label} (not loaded)`);
                    continue;
                }
                // Matching variants are retextures of the same geometry, so the
                // world-space planes transfer as-is; others only take the rotation.
                const matching = geometryGroupKey(item, v) === groupKey;
                if (!matching && !rotationChanged) {
                    skipped.push(`${label} (unchanged)`);
                    continue;
                }
                progress(label);
                try {
                    entry.root.rotationQuaternion = rotationQ.clone();
                    let planeList = planesByVariant[v.id] || [];
                    if (matching) {
                        entry.planeMeshes.forEach((m) => m.dispose());
                        entry.planeMeshes.clear();
                        planeList = planeSpecs.map((spec) => {
                            const id = `p${seqRef.current++}`;
                            makePlane(entry, id, spec.width, spec.depth, spec.position);
                            return { id, name: spec.name, width: spec.width, depth: spec.depth, mode: 'xz' };
                        });
                        entry.planeMeshes.forEach((m) => m.setEnabled(false));
                        setPlanesByVariant((prev) => ({ ...prev, [v.id]: planeList }));
                        if (bedSpec) {
                            const { position, blanketOffset, rotation, ...spec } = bedSpec;
                            entry.modelBedMeshes.forEach((m) => {
                                entry.exportNodes.delete(m);
                                m.dispose();
                            });
                            entry.modelBedMeshes = [];
                            makeBed(entry, spec, position, blanketOffset, rotation);
                            entry.bed.group.setEnabled(false);
                            setBedByVariant((prev) => ({ ...prev, [v.id]: { kind: 'editable', ...spec } }));
                        }
                    }
                    startUpload(v.id, label, await exportVariantGlb(v.id, planeList));
                } catch {
                    skipped.push(`${label} (export failed)`);
                }
            }

            message.loading({
                content: `Uploading ${uploads.length} model${uploads.length > 1 ? 's' : ''}…`,
                key: SAVE_ALL_KEY,
                duration: 0,
            });
            const results = await Promise.all(uploads);
            const failed = results.filter((r) => !r.ok).map((r) => r.label);
            const applied = results.filter((r) => r.ok).length;

            message.destroy(SAVE_ALL_KEY);
            const parts = [`Saved ${applied} variant${applied === 1 ? '' : 's'}`];
            if (skipped.length) parts.push(`skipped: ${skipped.join(', ')}`);
            if (failed.length) parts.push(`failed: ${failed.join(', ')}`);
            (failed.length ? message.warning : message.success)(parts.join(' · '), 6);
            onSaved?.();
        } catch (e) {
            message.destroy(SAVE_ALL_KEY);
            message.error(e.message || 'Failed to save');
        } finally {
            setSavingAll(false);
        }
    };

    const anyLoading = Object.values(modelStates).some((s) => s.status === 'loading');
    const activeError = modelStates[activeId]?.error;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0', flexShrink: 0, overflowX: 'auto' }}>
                {menuVariants.length === 0 ? (
                    <Text type="secondary">No variant has a .glb model yet.</Text>
                ) : (
                    <Radio.Group
                        value={activeId}
                        onChange={(e) => setActiveId(e.target.value)}
                        optionType="button"
                        buttonStyle="solid"
                        size="small"
                    >
                        {menuVariants.map((v) => {
                            const st = modelStates[v.id];
                            return (
                                <Radio.Button key={v.id} value={v.id} disabled={st?.status !== 'ready'}>
                                    {variationLabel(item, v)}
                                    {st?.status === 'loading' && <LoadingOutlined style={{ marginLeft: 6 }} />}
                                    {st?.status === 'error' && (
                                        <Tooltip title={st.error}>
                                            <Tag color="error" style={{ marginLeft: 6, marginInlineEnd: 0 }}>
                                                failed
                                            </Tag>
                                        </Tooltip>
                                    )}
                                </Radio.Button>
                            );
                        })}
                    </Radio.Group>
                )}
            </div>

            <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
                <div style={{ position: 'relative', flex: 1, minWidth: 0, background: '#fafafa' }}>
                    <canvas
                        ref={canvasRef}
                        style={{ width: '100%', height: '100%', outline: 'none', display: 'block' }}
                    />
                    {menuVariants.length === 0 && (
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Empty description="No models to edit" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        </div>
                    )}
                    {!activeReady && anyLoading && (
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Spin tip="Loading models…" />
                        </div>
                    )}
                    {activeError && (
                        <Alert type="error" message={activeError} showIcon style={{ position: 'absolute', top: 8, left: 8, right: 8 }} />
                    )}
                </div>

                <div
                    style={{
                        width: 380,
                        flexShrink: 0,
                        borderLeft: '1px solid #f0f0f0',
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                    }}
                >
                    <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>Model bounding box</Text>
                        <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                            <Space>
                                <DimCell label="width (X)" value={dims?.width} />
                                <DimCell label="height (Y)" value={dims?.height} />
                                <DimCell label="depth (Z)" value={dims?.depth} />
                            </Space>
                            <Button size="small" danger icon={<UndoOutlined />} onClick={resetRotation} disabled={!dims}>
                                Reset rotation
                            </Button>
                        </div>
                    </div>

                    {isBed && (
                        <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Text strong>Bedding</Text>
                                {!bed && (
                                    <Tooltip
                                        title={
                                            bedTemplatesState === 'error'
                                                ? 'The default mattress could not be loaded'
                                                : bedTemplatesState !== 'ready'
                                                  ? 'Loading the default mattress…'
                                                  : undefined
                                        }
                                    >
                                        <Button
                                            icon={<PlusOutlined />}
                                            onClick={addBed}
                                            disabled={!dims || bedTemplatesState !== 'ready'}
                                            loading={bedTemplatesState === 'loading'}
                                        >
                                            Add mattress
                                        </Button>
                                    </Tooltip>
                                )}
                            </div>
                            {!bed && (
                                <Alert
                                    style={{ marginTop: 8 }}
                                    type="warning"
                                    showIcon
                                    message="Beds need a mattress mesh — sheets and blankets are applied to it in the editor."
                                />
                            )}
                            {bed?.kind === 'model' && (
                                <Space direction="vertical" size={6} style={{ width: '100%', marginTop: 8 }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        This model already carries its own mattress
                                        {(modelsRef.current.get(activeId)?.modelBedMeshes || []).some(
                                            (m) => isBlanketMeshName(m.name) && !m.name.includes(INVISIBLE_MARKER),
                                        )
                                            ? ' and blanket'
                                            : ''}
                                        ; they are saved as they are.
                                    </Text>
                                    <Button size="small" onClick={replaceModelBed} disabled={bedTemplatesState !== 'ready'}>
                                        Replace with the editable defaults
                                    </Button>
                                </Space>
                            )}
                            {bed?.kind === 'editable' && (
                                <div
                                    onClick={() => setSelectedId(BED_ID)}
                                    style={{
                                        marginTop: 8,
                                        border: selectedId === BED_ID ? '1px solid #ff4d4f' : '1px solid #f0f0f0',
                                        borderRadius: 6,
                                        padding: 8,
                                        cursor: 'pointer',
                                    }}
                                >
                                    <Space direction="vertical" size={6} style={{ width: '100%' }}>
                                        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                            {selectedId === BED_ID ? <Tag color="error">selected</Tag> : <Tag>mattress</Tag>}
                                            <Button
                                                size="small"
                                                type="text"
                                                danger
                                                icon={<DeleteOutlined />}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeBed();
                                                }}
                                            />
                                        </Space>
                                        <Space size={4}>
                                            <Text style={{ fontSize: 12 }}>Arrows move:</Text>
                                            <Button
                                                size="small"
                                                type={bed.mode === 'xz' ? 'primary' : 'default'}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedId(BED_ID);
                                                    updateBed({ mode: 'xz' });
                                                }}
                                            >
                                                Ground (XZ)
                                            </Button>
                                            <Button
                                                size="small"
                                                type={bed.mode === 'y' ? 'primary' : 'default'}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedId(BED_ID);
                                                    updateBed({ mode: 'y' });
                                                }}
                                            >
                                                Height (Y)
                                            </Button>
                                        </Space>
                                        <Space>
                                            {[
                                                ['width', 'Width (X)'],
                                                ['depth', 'Depth (Z)'],
                                                ['height', 'Height (Y)'],
                                            ].map(([key, label]) => (
                                                <span key={key}>
                                                    <div><Text style={{ fontSize: 12 }}>{label}</Text></div>
                                                    <InputNumber
                                                        size="small"
                                                        min={0.01}
                                                        step={0.1}
                                                        value={bed[key]}
                                                        onChange={(v) => updateBed({ [key]: v || 0.01 })}
                                                        onClick={(e) => e.stopPropagation()}
                                                        style={{ width: 90 }}
                                                    />
                                                </span>
                                            ))}
                                        </Space>
                                        <Text type="secondary" style={{ fontSize: 11 }}>
                                            Saved as <code>{MATTRESS_MESH_NAME}</code>. While selected, the gizmo rings turn the
                                            bedding instead of the model. Catalog beds lie along X with the head end at +X (to the
                                            right when facing FRONT).
                                        </Text>
                                    </Space>
                                </div>
                            )}
                            {bed?.kind === 'editable' && (
                                <div
                                    onClick={() => bed.blanket && setSelectedId(blanketSeparate ? BLANKET_ID : BED_ID)}
                                    style={{
                                        marginTop: 8,
                                        border: selectedId === BLANKET_ID ? '1px solid #ff4d4f' : '1px solid #f0f0f0',
                                        borderRadius: 6,
                                        padding: 8,
                                        cursor: bed.blanket ? 'pointer' : 'default',
                                        opacity: bed.blanket ? 1 : 0.6,
                                    }}
                                >
                                    <Space direction="vertical" size={6} style={{ width: '100%' }}>
                                        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                            {selectedId === BLANKET_ID ? <Tag color="error">selected</Tag> : <Tag>blanket</Tag>}
                                            <span onClick={(e) => e.stopPropagation()}>
                                                <Select
                                                    size="small"
                                                    style={{ width: 250 }}
                                                    value={bed.blanket ?? ''}
                                                    options={[
                                                        { value: '', label: 'No blanket' },
                                                        ...BLANKET_STYLES.map((s) => ({
                                                            value: s.id,
                                                            label: s.id === defaultBlanketStyle(item.itemType) ? `${s.label} (default)` : s.label,
                                                        })),
                                                    ]}
                                                    onChange={(v) => {
                                                        updateBed({ blanket: v || null });
                                                        setSelectedId((cur) => (cur === BLANKET_ID && !isSeparateBlanket(v) ? BED_ID : cur));
                                                    }}
                                                />
                                            </span>
                                        </Space>
                                        {bed.blanket && !blanketSeparate && (
                                            <Text type="secondary" style={{ fontSize: 11 }}>
                                                Drapes over the mattress and moves, sizes and turns with it. Saved as{' '}
                                                <code>{BLANKET_MESH_NAME}</code>
                                                {blanketHasContainer && (
                                                    <>
                                                        {' '}with an invisible <code>{BLANKET_CONTAINER_MESH_NAME}</code> on top
                                                    </>
                                                )}
                                                .
                                            </Text>
                                        )}
                                        {bed.blanket && blanketSeparate && (
                                            <>
                                                <Space size={4}>
                                                    <Text style={{ fontSize: 12 }}>Arrows move:</Text>
                                                    <Button
                                                        size="small"
                                                        type={bed.blanketMode === 'xz' ? 'primary' : 'default'}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedId(BLANKET_ID);
                                                            updateBed({ blanketMode: 'xz' });
                                                        }}
                                                    >
                                                        Ground (XZ)
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        type={bed.blanketMode === 'y' ? 'primary' : 'default'}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedId(BLANKET_ID);
                                                            updateBed({ blanketMode: 'y' });
                                                        }}
                                                    >
                                                        Height (Y)
                                                    </Button>
                                                </Space>
                                                <Text type="secondary" style={{ fontSize: 11 }}>
                                                    Hangs on the rail: sized with the mattress but placed on its own — nudge it into
                                                    place. Saved as <code>{BLANKET_MESH_NAME}</code>
                                                    {blanketHasContainer && (
                                                        <>
                                                            {' '}with an invisible <code>{BLANKET_CONTAINER_MESH_NAME}</code> on top
                                                        </>
                                                    )}
                                                    .
                                                </Text>
                                            </>
                                        )}
                                    </Space>
                                </div>
                            )}
                        </div>
                    )}

                    <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Text strong>Container planes ({planes.length})</Text>
                        <Button icon={<PlusOutlined />} onClick={addPlane} disabled={!dims}>
                            Add plane
                        </Button>
                    </div>

                    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 16px 16px' }}>
                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            {planes.length === 0 && <Text type="secondary">No planes yet — click “Add plane”.</Text>}

                            {planes.map((p) => {
                                const active = p.id === selectedId;
                                return (
                                    <div
                                        key={p.id}
                                        onClick={() => setSelectedId(p.id)}
                                        style={{
                                            border: active ? '1px solid #ff4d4f' : '1px solid #f0f0f0',
                                            borderRadius: 6,
                                            padding: 8,
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <Space direction="vertical" size={6} style={{ width: '100%' }}>
                                            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                                {active ? <Tag color="error">selected</Tag> : <Tag>plane</Tag>}
                                                <Button
                                                    size="small"
                                                    type="text"
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        removePlane(p.id);
                                                    }}
                                                />
                                            </Space>
                                            <Input
                                                size="small"
                                                value={p.name}
                                                onChange={(e) => updatePlane(p.id, { name: e.target.value })}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                            {!isContainerInvisible(p.name) && (
                                                <Text type="warning" style={{ fontSize: 11 }}>
                                                    Name should start with <code>$$container_</code> and include <code>invisible</code>.
                                                </Text>
                                            )}
                                            <Space size={4}>
                                                <Text style={{ fontSize: 12 }}>Arrows move:</Text>
                                                <Button
                                                    size="small"
                                                    type={p.mode === 'xz' ? 'primary' : 'default'}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedId(p.id);
                                                        updatePlane(p.id, { mode: 'xz' });
                                                    }}
                                                >
                                                    Ground (XZ)
                                                </Button>
                                                <Button
                                                    size="small"
                                                    type={p.mode === 'y' ? 'primary' : 'default'}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedId(p.id);
                                                        updatePlane(p.id, { mode: 'y' });
                                                    }}
                                                >
                                                    Height (Y)
                                                </Button>
                                            </Space>
                                            <Space>
                                                <span>
                                                    <div><Text style={{ fontSize: 12 }}>Width (X)</Text></div>
                                                    <InputNumber
                                                        size="small"
                                                        min={0.01}
                                                        step={0.1}
                                                        value={p.width}
                                                        onChange={(v) => updatePlane(p.id, { width: v || 0.01 })}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </span>
                                                <span>
                                                    <div><Text style={{ fontSize: 12 }}>Depth (Z)</Text></div>
                                                    <InputNumber
                                                        size="small"
                                                        min={0.01}
                                                        step={0.1}
                                                        value={p.depth}
                                                        onChange={(v) => updatePlane(p.id, { depth: v || 0.01 })}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </span>
                                            </Space>
                                        </Space>
                                    </div>
                                );
                            })}
                        </Space>
                    </div>

                    <div style={{ padding: 16, borderTop: '1px solid #f0f0f0' }}>
                        <Button
                            block
                            type="primary"
                            icon={<SaveOutlined />}
                            loading={saving}
                            disabled={!activeReady || savingAll}
                            onClick={handleSave}
                        >
                            Save as new model
                        </Button>
                        {otherVariants.length > 0 && (
                            <Tooltip title="Also applies this rotation to every other variant's model, copies these planes (and the bedding) to same-geometry variants, and saves each as a new .glb">
                                <Button
                                    block
                                    icon={<SaveOutlined />}
                                    loading={savingAll}
                                    disabled={!activeReady || saving}
                                    onClick={handleSaveAll}
                                    style={{ marginTop: 8 }}
                                >
                                    Save + apply to {otherVariants.length} other variant{otherVariants.length > 1 ? 's' : ''}
                                </Button>
                            </Tooltip>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export function ModelEditor({ open, item, assetsByVariant, onClose, onSaved }) {
    return (
        <Drawer
            title="Model editor"
            width="100%"
            open={open}
            onClose={onClose}
            // Keep the editor mounted while closed: the engine boots and the models
            // preload as soon as the assets step renders, so opening is instant.
            forceRender
            styles={{ body: { padding: 0, overflow: 'hidden' } }}
        >
            <EditorBody key={item.id} item={item} assetsByVariant={assetsByVariant} onSaved={onSaved} />
        </Drawer>
    );
}
